import React, { useState, useEffect, useRef } from "react";
import { lightTheme, darkTheme } from "./theme";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import Home from "./components/home/Home";
import Contact from "./components/Contact";
import About from "./components/About";
import Services from "./components/Services";
import Resources from "./components/Resources";
import Pricing from "./components/Pricing";
import ProductPage from "./components/ProductPage";
import ProductSamplePage from "./components/ProductSamplePage";
import CookieConsent from "./components/CookieConsent";
import LoginPage from "./components/login/LoginPage";
import CreateAccountOptions from "./createAccount/createaccountoptions/CreateAccountOptions";
import CreateAccountForm from "./createAccount/createaccountform/CreateAccountForm";
import ToggleDrawer from "./components/ToggleDrawer";
import Appbar from "./components/Appbar";
import MainPage from "./components/mainpage/MainPage";
import LessonPage from "./components/lessons/LessonPage";
import CoursePage from "./components/courses/ViewCoursePage";
import SignIn from "./components/Signin";
import QuizPage from "./components/quiz/QuizPage";
import Assignment from "./components/assignments/Assignment";
import Enroll from "./components/Enroll";

import {
  ThemeProvider,
  CssBaseline,
  Drawer,
  Container,
  Box,
  useMediaQuery,
  useForkRef,
} from "@mui/material";
import { Quiz } from "@mui/icons-material";

const drawerWidth = 240;
const App = () => {
  const [themeMode, setThemeMode] = useState("light");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showAppBar, setShowAppBar] = useState(true); // AppBar visibility state
  const isMobile = useMediaQuery("(max-width:825px)");
  const aboutRef = useRef(null);
  const instRef = useRef(null);
  const testimonialRef = useRef(null);
  const compareProRef = useRef(null);
  const QARef = useRef(null);
  const contactRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLoginSuccess = (data) => {
    setUserInfo(data);
  };
  const handleLogout = () => {
    setUserInfo(null);
    window.location.href = "/"; // Redirect to home page
  };

  const ProtectedRoute = ({ children }) => {
    if (!userInfo) {
      // Redirect the user to the Google login page if they're not authenticated
      window.location.href =
        "https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?client_id=106972638172-0hmocdv6qc4755jn5sgf6dm97e45j10u.apps.googleusercontent.com&redirect_uri=http%3A%2F%2Flocalhost%3A3000&response_type=token&scope=profile%20email&service=lso&o2v=2&ddm=1&flowName=GeneralOAuthFlow";
      return null; // We return null because we're redirecting
    }
    return children;
  };
  const toggleTheme = () => {
    setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
  };
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  return (
    <ThemeProvider theme={themeMode === "light" ? lightTheme : darkTheme}>
      <CssBaseline />
      <CookieConsent />
      <Router>
        {showAppBar && (
          <Appbar
            isMobile={isMobile}
            handleDrawerToggle={handleDrawerToggle}
            handleLogout={handleLogout}
            userInfo={userInfo}
            handleLoginSuccess={handleLoginSuccess}
            themeMode={themeMode}
            toggleTheme={toggleTheme}
            onAboutClick={() => {
              scrollToSection(aboutRef);
            }}
            onInstClick={() => {
              scrollToSection(instRef);
            }}
            ontestimonialClick={() => {
              scrollToSection(testimonialRef);
            }}
            onCompareProClick={() => {
              scrollToSection(compareProRef);
            }}
            onQARefClick={() => {
              scrollToSection(QARef);
            }}
            onContactRefClick={() => {
              scrollToSection(contactRef);
            }}
          />
        )}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          <ToggleDrawer
            handleDrawerToggle={handleDrawerToggle}
            mobileOpen={mobileOpen}
            onAboutClick={() => {
              scrollToSection(aboutRef);
            }}
            onInstClick={() => {
              scrollToSection(instRef);
            }}
            ontestimonialClick={() => {
              scrollToSection(testimonialRef);
            }}
            onCompareProClick={() => {
              scrollToSection(compareProRef);
            }}
            onQARefClick={() => {
              scrollToSection(QARef);
            }}
            onContactRefClick={() => {
              scrollToSection(contactRef);
            }}
          />
        </Drawer>

        <Routes>
          <Route
            path="/"
            element={
              <Home
                aboutRef={aboutRef}
                instRef={instRef}
                testimonialRef={testimonialRef}
                compareProRef={compareProRef}
                QARef={QARef}
                contactRef={contactRef}
              />
            }
          />
          <Route path="/enroll" element={<Enroll />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/services" element={<Services />} />
          <Route path="/productsamplepage" element={<ProductSamplePage />} />
          <Route path="Signin" element={<SignIn />} />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <Pricing />
              </ProtectedRoute>
            }
          />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route
            path="/loginpage"
            element={<LoginPage setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/createaccountoptions"
            element={<CreateAccountOptions setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/Createaccountform"
            element={<CreateAccountForm setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/coursepage"
            element={<CoursePage setShowAppBar={setShowAppBar} />}
          />
          <Route
            path="/Modules/Course/:courseId"
            element={<MainPage setShowAppBar={setShowAppBar} />}
          >
            {/* Nested route should be relative */}
            <Route
              path="Module/:moduleId/Chapter/:chapterId/Lesson/:lessonId"
              element={<LessonPage />}
            >
              <Route path="quizpage" element={<QuizPage />} />
              <Route path="assignment" element={<Assignment />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
